import React, {useMemo} from "react";
import {useSelector} from "react-redux";

import {isEmpty, sum, upperFirst} from "@pg-mono/nodash";

import {IRPStore} from "../app/rp_reducer";
import {useLocationLinksCheck} from "../hooks/use_location_links_check";
import {OfferLocationLinksFloorChoices} from "../offer/atoms/OfferLocationLinksFloorChoices";
import {OfferLocationLinksList} from "../offer/atoms/OfferLocationLinksList";
import {OfferLocationLinksMixed} from "../offer/atoms/OfferLocationLinksMixed";
import {IOfferLocationLinksOtherOffersProps, OfferLocationLinksOtherOffers} from "../offer/atoms/OfferLocationLinksOtherOffers";
import {IRecommendedRegion, OfferLocationLinksRecommendedRegions} from "../offer/atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksRegionHouse} from "../offer/atoms/OfferLocationLinksRegionHouse";
import {OfferLocationLinksRegionRooms} from "../offer/atoms/OfferLocationLinksRegionRooms";
import {OfferDetailLocationLinksRelatedOffers} from "../offer/detail/components/OfferDetailLocationLinksRelatedOffers";
import {getOfferTypeEnglishNamePlural, getPropertiesCountFieldName} from "../offer/helpers/OfferType";
import {IRegionStats} from "../offer/list/actions/offer_list_selected_data_actions";
import {INearbyRegion, OfferListLocationLinksNearbyRegions} from "../offer/list/components/OfferListLocationLinks/OfferListLocationLinksNearbyRegions";
import {OfferListLocationVoivodeshipTopCitiesLinks} from "../offer/list/components/OfferListLocationLinks/OfferListLocationVoivodeshipTopCitiesLinks";
import {getCountryName} from "../offer/list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../offer/types/OfferType";
import {Country} from "../region/types/Country";
import {RegionType} from "../region/types/RegionType";

export interface IRegions {
    id: number;
    name: string;
    name_declension_where: string;
    name_declension_what: string;
    short_name: string;
    slug: string;
    stats:
        | (Pick<
              IRegionStats,
              | "properties_commercials_rooms_ranges"
              | "properties_all_rooms_ranges"
              | "properties_flats_rooms_ranges"
              | "properties_flats_ground_floor_with_garden"
              | "properties_flats_ground_floor"
              | "properties_flats_last_floor"
              | "properties_houses_rooms_ranges"
              | "properties_houses_serial_count"
              | "properties_houses_detached_count"
              | "properties_houses_semi_detached_count"
              | "properties_houses_additional_areas_attic"
              | "properties_all_count_for_sale"
              | "properties_flats_count_for_sale"
              | "properties_houses_count_for_sale"
              | "properties_commercials_count_for_sale"
          > &
              IOfferLocationLinksOtherOffersProps["region"]["stats"])
        | null;
    type: RegionType;
    country?: number | null;
}

export interface IProps {
    recommendedRegions: IRecommendedRegion[];
    nearbyRegions?: INearbyRegion[];
    offerType: OfferType | null;
    isOfferListEmpty?: boolean;
    region?: IRegions;
    country?: Country;
    abroadVoivodeshipRegions?: boolean;
    relatedOffers?: {
        vendor: {
            id: number;
            slug: string;
        };
        slug: string;
        id: number;
        name: string;
        stats: {
            properties_count_for_sale: number;
        } | null;
    }[];
}

export type PropertiesRoomsAvailableType =
    | "properties_all_rooms_ranges"
    | "properties_flats_rooms_ranges"
    | "properties_houses_rooms_ranges"
    | "properties_commercials_rooms_ranges";

export const LocationLinks: React.FC<IProps> = (props) => {
    const {offerType, recommendedRegions, region, nearbyRegions, relatedOffers, abroadVoivodeshipRegions} = props;

    const countryName = upperFirst(getCountryName(props.country));

    const panelTitles = {
        NEARBY_LOCATIONS: "Pobliskie lokalizacje",
        OFFERS_IN_REGION: "Oferty",
        RECOMMENDED_LOCATIONS: "Polecane lokalizacje",
        RELATED_SEARCHES: "Powiązane wyszukiwania",
        RELATED_OFFERS: "Inwestycje w pobliżu",
        HOUSE_OFFERS_ABROAD: `Domy ${countryName}`,
        FLATS_OFFERS_ABROAD: `Mieszkania ${countryName}`,
        OFFERS_ABROAD: `Oferty ${countryName}`
    };
    const voivodeshipTopCities = useSelector((state: IRPStore) => state.offerList.voivodeshipTopCities);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const isVoivodeshipRegion = selectedRegions && selectedRegions.length > 0 && selectedRegions[0].type === RegionType.VOIVODESHIP;
    const roomsRecords =
        region &&
        region.stats &&
        region.stats[`properties_${offerType ? getOfferTypeEnglishNamePlural(offerType) : "all"}_rooms_ranges` as PropertiesRoomsAvailableType];
    const lastQuery = useSelector((store: IRPStore) => store.offerList.list.latestQuery);
    const {otherOffersLink, regionAvailableRoomsLink, availableFloorTypes} = useLocationLinksCheck(lastQuery, offerType, viewType, region);
    const abroadLinksTitle = () => {
        switch (offerType) {
            case OfferType.HOUSE:
                return panelTitles.HOUSE_OFFERS_ABROAD;
            case OfferType.FLAT:
                return panelTitles.FLATS_OFFERS_ABROAD;
            default:
                return panelTitles.OFFERS_ABROAD;
        }
    };

    const oneToFiveRoomsRanges = useMemo(
        () =>
            Object.entries(roomsRecords || {}).reduce((acc: Record<number, number>, [key, value]) => {
                if (parseInt(key, 10) > 5) {
                    return {
                        ...acc,
                        5: acc[5] + value
                    };
                }
                return {...acc, [key]: value};
            }, {}),
        [roomsRecords]
    );

    const someRecommendedRegionsOffer = recommendedRegions.some((region) => (region.stats ? region.stats[getPropertiesCountFieldName(offerType)] : false));
    const someNearbyRegionsOffer = nearbyRegions?.some((region) => region?.stats && region.stats?.[getPropertiesCountFieldName(offerType)]);
    const someVoivodeshipTopCitiesOffer = voivodeshipTopCities?.some((city) => city.properties_count > 0);

    const houseStats = {
        properties_houses_serial_count: region?.stats?.properties_houses_serial_count || 0,
        properties_houses_detached_count: region?.stats?.properties_houses_detached_count || 0,
        properties_houses_semi_detached_count: region?.stats?.properties_houses_semi_detached_count || 0,
        properties_houses_additional_areas_attic: region?.stats?.properties_houses_additional_areas_attic || 0
    };
    const houseOffersInRegion = useMemo(() => {
        if (!region || !region.stats) {
            return 0;
        }

        return sum(Object.values(houseStats));
    }, [props.region]);

    return (
        <>
            {abroadVoivodeshipRegions && someRecommendedRegionsOffer && props.country !== Country.POLAND && (
                <OfferLocationLinksList title={abroadLinksTitle()}>
                    <OfferLocationLinksMixed country={props.country} recommendedRegions={recommendedRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {someRecommendedRegionsOffer && !abroadVoivodeshipRegions && (
                <OfferLocationLinksList title={panelTitles.NEARBY_LOCATIONS}>
                    <OfferLocationLinksRecommendedRegions country={region?.country} recommendedRegions={recommendedRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {relatedOffers && relatedOffers.length > 0 && region && region.country === Country.POLAND && (
                <OfferLocationLinksList title={`${panelTitles.RELATED_OFFERS} ${region.name_declension_where}`}>
                    <OfferDetailLocationLinksRelatedOffers relatedOffers={relatedOffers} />
                </OfferLocationLinksList>
            )}

            {isVoivodeshipRegion && someVoivodeshipTopCitiesOffer && (
                <OfferLocationLinksList title={`${panelTitles.RECOMMENDED_LOCATIONS}`}>
                    <OfferListLocationVoivodeshipTopCitiesLinks country={region?.country} offerType={offerType} voivodeshipTopCities={voivodeshipTopCities} />
                </OfferLocationLinksList>
            )}

            {nearbyRegions && region && someNearbyRegionsOffer && (
                <OfferLocationLinksList title={`${panelTitles.RECOMMENDED_LOCATIONS}`}>
                    <OfferListLocationLinksNearbyRegions country={region?.country} nearbyRegions={nearbyRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {region && region.country === Country.POLAND && !props.isOfferListEmpty && (
                <>
                    {regionAvailableRoomsLink.hasLinks && ![OfferType.COMMERCIAL, null].includes(props.offerType) && (
                        <OfferLocationLinksList title={`${panelTitles.OFFERS_IN_REGION} ${region.name_declension_where}`}>
                            {!isEmpty(oneToFiveRoomsRanges) ? (
                                <OfferLocationLinksRegionRooms region={region} offerType={offerType} oneToFiveRoomsRanges={oneToFiveRoomsRanges} />
                            ) : null}
                            {props.offerType === OfferType.FLAT && region.type === RegionType.CITY && availableFloorTypes.length > 0 ? (
                                <OfferLocationLinksFloorChoices
                                    offerType={offerType}
                                    regionName={region.name}
                                    regionSlug={region.slug}
                                    availableFloorTypes={availableFloorTypes}
                                />
                            ) : null}
                            {props.offerType === OfferType.HOUSE && region.type === RegionType.CITY && houseOffersInRegion > 0 ? (
                                <OfferLocationLinksRegionHouse region={region} offerType={offerType} houseStats={houseStats} />
                            ) : null}
                        </OfferLocationLinksList>
                    )}
                    {otherOffersLink.length > 0 && (
                        <OfferLocationLinksList title={panelTitles.RELATED_SEARCHES}>
                            {otherOffersLink.map((variant) => (
                                <OfferLocationLinksOtherOffers key={variant} region={region} offerType={offerType} variant={variant} />
                            ))}
                        </OfferLocationLinksList>
                    )}
                </>
            )}
        </>
    );
};
import React, {useMemo} from "react";
import {useSelector} from "react-redux";

import {isEmpty, sum, upperFirst} from "@pg-mono/nodash";

import {IRPStore} from "../app/rp_reducer";
import {useLocationLinksCheck} from "../hooks/use_location_links_check";
import {OfferLocationLinksFloorChoices} from "../offer/atoms/OfferLocationLinksFloorChoices";
import {OfferLocationLinksList} from "../offer/atoms/OfferLocationLinksList";
import {OfferLocationLinksMixed} from "../offer/atoms/OfferLocationLinksMixed";
import {IOfferLocationLinksOtherOffersProps, OfferLocationLinksOtherOffers} from "../offer/atoms/OfferLocationLinksOtherOffers";
import {IRecommendedRegion, OfferLocationLinksRecommendedRegions} from "../offer/atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksRegionHouse} from "../offer/atoms/OfferLocationLinksRegionHouse";
import {OfferLocationLinksRegionRooms} from "../offer/atoms/OfferLocationLinksRegionRooms";
import {OfferDetailLocationLinksRelatedOffers} from "../offer/detail/components/OfferDetailLocationLinksRelatedOffers";
import {getOfferTypeEnglishNamePlural, getPropertiesCountFieldName} from "../offer/helpers/OfferType";
import {IRegionStats} from "../offer/list/actions/offer_list_selected_data_actions";
import {INearbyRegion, OfferListLocationLinksNearbyRegions} from "../offer/list/components/OfferListLocationLinks/OfferListLocationLinksNearbyRegions";
import {OfferListLocationVoivodeshipTopCitiesLinks} from "../offer/list/components/OfferListLocationLinks/OfferListLocationVoivodeshipTopCitiesLinks";
import {getCountryName} from "../offer/list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../offer/types/OfferType";
import {Country} from "../region/types/Country";
import {RegionType} from "../region/types/RegionType";

export interface IRegions {
    id: number;
    name: string;
    name_declension_where: string;
    name_declension_what: string;
    short_name: string;
    slug: string;
    stats:
        | (Pick<
              IRegionStats,
              | "properties_commercials_rooms_ranges"
              | "properties_all_rooms_ranges"
              | "properties_flats_rooms_ranges"
              | "properties_flats_ground_floor_with_garden"
              | "properties_flats_ground_floor"
              | "properties_flats_last_floor"
              | "properties_houses_rooms_ranges"
              | "properties_houses_serial_count"
              | "properties_houses_detached_count"
              | "properties_houses_semi_detached_count"
              | "properties_houses_additional_areas_attic"
              | "properties_all_count_for_sale"
              | "properties_flats_count_for_sale"
              | "properties_houses_count_for_sale"
              | "properties_commercials_count_for_sale"
          > &
              IOfferLocationLinksOtherOffersProps["region"]["stats"])
        | null;
    type: RegionType;
    country?: number | null;
}

export interface IProps {
    recommendedRegions: IRecommendedRegion[];
    nearbyRegions?: INearbyRegion[];
    offerType: OfferType | null;
    isOfferListEmpty?: boolean;
    region?: IRegions;
    country?: Country;
    abroadVoivodeshipRegions?: boolean;
    relatedOffers?: {
        vendor: {
            id: number;
            slug: string;
        };
        slug: string;
        id: number;
        name: string;
        stats: {
            properties_count_for_sale: number;
        } | null;
    }[];
}

export type PropertiesRoomsAvailableType =
    | "properties_all_rooms_ranges"
    | "properties_flats_rooms_ranges"
    | "properties_houses_rooms_ranges"
    | "properties_commercials_rooms_ranges";

export const LocationLinks: React.FC<IProps> = (props) => {
    const {offerType, recommendedRegions, region, nearbyRegions, relatedOffers, abroadVoivodeshipRegions} = props;

    const countryName = upperFirst(getCountryName(props.country));

    const panelTitles = {
        NEARBY_LOCATIONS: "Pobliskie lokalizacje",
        OFFERS_IN_REGION: "Oferty",
        RECOMMENDED_LOCATIONS: "Polecane lokalizacje",
        RELATED_SEARCHES: "Powiązane wyszukiwania",
        RELATED_OFFERS: "Inwestycje w pobliżu",
        HOUSE_OFFERS_ABROAD: `Domy ${countryName}`,
        FLATS_OFFERS_ABROAD: `Mieszkania ${countryName}`,
        OFFERS_ABROAD: `Oferty ${countryName}`
    };
    const voivodeshipTopCities = useSelector((state: IRPStore) => state.offerList.voivodeshipTopCities);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const isVoivodeshipRegion = selectedRegions && selectedRegions.length > 0 && selectedRegions[0].type === RegionType.VOIVODESHIP;
    const roomsRecords =
        region &&
        region.stats &&
        region.stats[`properties_${offerType ? getOfferTypeEnglishNamePlural(offerType) : "all"}_rooms_ranges` as PropertiesRoomsAvailableType];
    const lastQuery = useSelector((store: IRPStore) => store.offerList.list.latestQuery);
    const {otherOffersLink, regionAvailableRoomsLink, availableFloorTypes} = useLocationLinksCheck(lastQuery, offerType, viewType, region);
    const abroadLinksTitle = () => {
        switch (offerType) {
            case OfferType.HOUSE:
                return panelTitles.HOUSE_OFFERS_ABROAD;
            case OfferType.FLAT:
                return panelTitles.FLATS_OFFERS_ABROAD;
            default:
                return panelTitles.OFFERS_ABROAD;
        }
    };

    const oneToFiveRoomsRanges = useMemo(
        () =>
            Object.entries(roomsRecords || {}).reduce((acc: Record<number, number>, [key, value]) => {
                if (parseInt(key, 10) > 5) {
                    return {
                        ...acc,
                        5: acc[5] + value
                    };
                }
                return {...acc, [key]: value};
            }, {}),
        [roomsRecords]
    );

    const someRecommendedRegionsOffer = recommendedRegions.some((region) => (region.stats ? region.stats[getPropertiesCountFieldName(offerType)] : false));
    const someNearbyRegionsOffer = nearbyRegions?.some((region) => region?.stats && region.stats?.[getPropertiesCountFieldName(offerType)]);
    const someVoivodeshipTopCitiesOffer = voivodeshipTopCities?.some((city) => city.properties_count > 0);

    const houseStats = {
        properties_houses_serial_count: region?.stats?.properties_houses_serial_count || 0,
        properties_houses_detached_count: region?.stats?.properties_houses_detached_count || 0,
        properties_houses_semi_detached_count: region?.stats?.properties_houses_semi_detached_count || 0,
        properties_houses_additional_areas_attic: region?.stats?.properties_houses_additional_areas_attic || 0
    };
    const houseOffersInRegion = useMemo(() => {
        if (!region || !region.stats) {
            return 0;
        }

        return sum(Object.values(houseStats));
    }, [props.region]);

    return (
        <>
            {abroadVoivodeshipRegions && someRecommendedRegionsOffer && props.country !== Country.POLAND && (
                <OfferLocationLinksList title={abroadLinksTitle()}>
                    <OfferLocationLinksMixed country={props.country} recommendedRegions={recommendedRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {someRecommendedRegionsOffer && !abroadVoivodeshipRegions && (
                <OfferLocationLinksList title={panelTitles.NEARBY_LOCATIONS}>
                    <OfferLocationLinksRecommendedRegions country={region?.country} recommendedRegions={recommendedRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {relatedOffers && relatedOffers.length > 0 && region && region.country === Country.POLAND && (
                <OfferLocationLinksList title={`${panelTitles.RELATED_OFFERS} ${region.name_declension_where}`}>
                    <OfferDetailLocationLinksRelatedOffers relatedOffers={relatedOffers} />
                </OfferLocationLinksList>
            )}

            {isVoivodeshipRegion && someVoivodeshipTopCitiesOffer && (
                <OfferLocationLinksList title={`${panelTitles.RECOMMENDED_LOCATIONS}`}>
                    <OfferListLocationVoivodeshipTopCitiesLinks country={region?.country} offerType={offerType} voivodeshipTopCities={voivodeshipTopCities} />
                </OfferLocationLinksList>
            )}

            {nearbyRegions && region && someNearbyRegionsOffer && (
                <OfferLocationLinksList title={`${panelTitles.RECOMMENDED_LOCATIONS}`}>
                    <OfferListLocationLinksNearbyRegions country={region?.country} nearbyRegions={nearbyRegions} offerType={offerType} />
                </OfferLocationLinksList>
            )}

            {region && region.country === Country.POLAND && !props.isOfferListEmpty && (
                <>
                    {regionAvailableRoomsLink.hasLinks && ![OfferType.COMMERCIAL, null].includes(props.offerType) && (
                        <OfferLocationLinksList title={`${panelTitles.OFFERS_IN_REGION} ${region.name_declension_where}`}>
                            {!isEmpty(oneToFiveRoomsRanges) ? (
                                <OfferLocationLinksRegionRooms region={region} offerType={offerType} oneToFiveRoomsRanges={oneToFiveRoomsRanges} />
                            ) : null}
                            {props.offerType === OfferType.FLAT && region.type === RegionType.CITY && availableFloorTypes.length > 0 ? (
                                <OfferLocationLinksFloorChoices
                                    offerType={offerType}
                                    regionName={region.name}
                                    regionSlug={region.slug}
                                    availableFloorTypes={availableFloorTypes}
                                />
                            ) : null}
                            {props.offerType === OfferType.HOUSE && region.type === RegionType.CITY && houseOffersInRegion > 0 ? (
                                <OfferLocationLinksRegionHouse region={region} offerType={offerType} houseStats={houseStats} />
                            ) : null}
                        </OfferLocationLinksList>
                    )}
                    {otherOffersLink.length > 0 && (
                        <OfferLocationLinksList title={panelTitles.RELATED_SEARCHES}>
                            {otherOffersLink.map((variant) => (
                                <OfferLocationLinksOtherOffers key={variant} region={region} offerType={offerType} variant={variant} />
                            ))}
                        </OfferLocationLinksList>
                    )}
                </>
            )}
        </>
    );
};
